import React, { useEffect, useState, useCallback } from "react";
import "./TeamSettings.css";
import { api } from "../../../services/api";
import FullScreenLoader from "../../FullScreenLoader/FullScreenLoader";

const ROLES = [
  { role: "Admin", key: "admin" },
  { role: "Gestor", key: "manager" },
  { role: "Operador", key: "operator" },
  { role: "Financeiro", key: "finance" },
];

const MODULES = [
  { label: "Dashboard", key: "dashboard" },
  { label: "CRM", key: "crm" },
  { label: "CMS", key: "cms" },
  { label: "Automação Atendimento", key: "service_automation" },
  { label: "Automação Tarefas", key: "task_automation" },
  { label: "Fluxos Inteligentes", key: "smart_flows" },
  { label: "Integrações", key: "integrations" },
  { label: "WhatsApp", key: "whatsapp" },
  { label: "Gestão Equipe", key: "team_operations" },
  { label: "Relatórios", key: "reports" },
  { label: "Logs", key: "logs" },
  { label: "Agente IA", key: "ai_agent" },
  { label: "Configurações", key: "settings" },
];

const buildEmptyMatrix = () => {
  const base = {};
  ROLES.forEach((r) => {
    base[r.key] = {};
    MODULES.forEach((m) => {
      base[r.key][m.key] = false;
    });
  });
  return base;
};

const TeamSettings = () => {
  const [permissions, setPermissions] = useState(buildEmptyMatrix);
  const [isLoading, setIsLoading] = useState(false);
  const [savingCell, setSavingCell] = useState(null); 
  const [error, setError] = useState("");

  useEffect(() => {
    const loadSettings = async () => {
      setIsLoading(true);
      setError("");

      try {
        const response = await api.get("/api/settings/team");
        const data = response.data || {};

        const matrix = buildEmptyMatrix();
        ROLES.forEach((r) => {
          MODULES.forEach((m) => {
            const valueFromApi = data?.[r.key]?.[m.key] ?? matrix[r.key][m.key];
            matrix[r.key][m.key] = Boolean(valueFromApi);
          });
        });

        setPermissions(matrix);
      } catch (err) {
        console.error(err);
        setError("Erro ao carregar permissões da equipe.");
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, []);

  const handleToggleChange = useCallback(
    async (roleKey, moduleKey, checked) => {
      setError("");
      const cellId = `${roleKey}:${moduleKey}`;
      setSavingCell(cellId);

      setPermissions((prev) => ({
        ...prev,
        [roleKey]: {
          ...prev[roleKey],
          [moduleKey]: checked,
        },
      }));

      try {
        await api.put("/api/settings/team", {
          role: roleKey,
          module: moduleKey,
          allowed: checked,
        });
      } catch (err) {
        console.error(err);
        setError("Erro ao salvar a permissão. Tente novamente.");

        // desfaz mudança em caso de erro
        setPermissions((prev) => ({
          ...prev,
          [roleKey]: {
            ...prev[roleKey],
            [moduleKey]: !checked,
          },
        }));
      } finally {
        setSavingCell(null);
      }
    },
    []
  );

  return (
    <div className="co-section">
      <h2>Equipe & permissões</h2>
      <p className="co-section-subtitle">
        Afeta:{" "}
        <strong>
          Operações da Equipe, WhatsApp Automation, Automação de Atendimento,
          Relatórios, Agente IA.
        </strong>
      </p>

      {error && <p className="co-error">{error}</p>}
      {isLoading && < FullScreenLoader message="Carregando Permissões Time..." />}

      <div className="co-group">
        <h3>Perfis de acesso</h3>
        <p className="co-hint">
          Defina quais módulos cada tipo de usuário pode ver / alterar.
        </p>

        <div className="co-permissions-wrapper">
          <div className="co-permissions-table">
            <div className="co-permissions-header">
              <span>Perfil</span>
              {MODULES.map((m) => (
                <span key={m.key}>{m.label}</span>
              ))}
            </div>

            {ROLES.map((r) => (
              <div key={r.key} className="co-permissions-row">
                <span className="co-role-name">{r.role}</span>

                {MODULES.map((m) => {
                  const checked = permissions[r.key]?.[m.key] ?? false;
                  const cellId = `${r.key}:${m.key}`;
                  const isSaving = savingCell === cellId;

                  return (
                    <span key={m.key} className="co-permission-cell">
                      <label className="co-toggle-small">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) =>
                            handleToggleChange(r.key, m.key, e.target.checked)
                          }
                          disabled={isSaving}
                        />
                        <span className="co-toggle-small-slider" />
                      </label>
                    </span>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* <div className="co-group co-group-internal">
        <h3>Regras internas de roteamento (somente admin)</h3>
        <div className="co-field">
          <label>Critério padrão de distribuição de tarefas</label>
          <select>
            <option>Balancear por capacidade (recomendado)</option>
            <option>Priorizar especialistas</option>
            <option>Round-robin entre membros do time</option>
          </select>
          <p className="co-hint">
            Afeta: Operações da Equipe, Automação de Tarefas, WhatsApp
            Automation.
          </p>
        </div>
      </div> */}
    </div>
  );
};

export default TeamSettings;
